import React, { useEffect, useState } from 'react';
import CustomSelect from '../common/CustomSelect';
import type {
  DomainPurchaseRecord,
  PurchaseHistoryResponse,
  PurchaseHistoryProps,
} from '../../types';

type SortField = 'purchased_at' | 'price';
type SortOrder = 'asc' | 'desc';

const PurchaseHistory: React.FC<PurchaseHistoryProps> = ({
  projectId,
  onViewDetails,
}) => {
  const [purchases, setPurchases] = useState<DomainPurchaseRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize] = useState<number>(10);

  // Filter state
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [purchasedByFilter, setPurchasedByFilter] = useState<string>('');
  const [dateRangeError, setDateRangeError] = useState<string | null>(null);

  // Sort state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  // Authentication check
  const authToken = localStorage.getItem('auth_token');

  const fetchPurchases = async () => {
    if (!authToken) {
      setError('Authentication required');
      setErrorDetail('Please log in');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setErrorDetail(null);

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('page_size', pageSize.toString());

      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (statusFilter) params.append('status', statusFilter);
      if (purchasedByFilter) params.append('purchased_by', purchasedByFilter);
      if (sortField) {
        params.append('sort_by', sortField);
        params.append('sort_order', sortOrder);
      }

      const url = `/api/v1/projects/${projectId}/godaddy/purchases?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (response.status === 403) {
          setError('Access denied');
          setErrorDetail('You do not have permission');
        } else {
          setError('Failed to load purchase history');
          setErrorDetail(errorData.detail || 'An error occurred');
        }
        setLoading(false);
        return;
      }

      const data: PurchaseHistoryResponse = await response.json();
      setPurchases(data.purchases);
      setTotalPages(data.total_pages);
      setTotalCount(data.total);
      setPage(data.page);
      setLoading(false);
    } catch (err) {
      setError('Failed to load purchase history');
      setErrorDetail(err instanceof Error ? err.message : 'Network error');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, page, sortField, sortOrder]);

  const handleApplyFilters = () => {
    // Validate date range
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      setDateRangeError('To Date must be after From Date');
      return;
    }
    setDateRangeError(null);
    setPage(1); // Reset to first page when filtering
    fetchPurchases();
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setStatusFilter('');
    setPurchasedByFilter('');
    setDateRangeError(null);
    setPage(1);
    setTimeout(() => fetchPurchases(), 0);
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleDomainClick = (purchaseId: number) => {
    if (onViewDetails) {
      onViewDetails(purchaseId);
    }
  };

  // Backend returns prices in cents
  const formatPrice = (price: number, currency: string): string => {
    const amount = (price / 100).toFixed(2);
    return `$${amount}`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusClass = (status: string): string => {
    const baseClasses = 'px-2 py-1 rounded text-xs font-semibold';
    switch (status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300 status-pending`;
      case 'failed':
        return `${baseClasses} bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300`;
      case 'refunded':
        return `${baseClasses} bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200`;
      default:
        return baseClasses;
    }
  };

  // Authentication error state
  if (!authToken) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Purchase History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-4 text-red-800">
          <p className="font-semibold">Authentication required</p>
          <p>Please log in</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading && purchases.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Purchase History</h2>
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-600 dark:text-gray-400">Loading purchases...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error && purchases.length === 0) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-4">Purchase History</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-4">
          <p className="font-semibold text-red-800">{error}</p>
          {errorDetail && <p className="text-red-600 mt-1">{errorDetail}</p>}
          {error === 'Failed to load purchase history' && (
            <button
              onClick={fetchPurchases}
              className="mt-3 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Purchase History</h2>

      {/* Filters */}
      <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label htmlFor="date-from" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From Date
            </label>
            <input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div>
            <label htmlFor="date-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To Date
            </label>
            <input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Filter by Status
            </label>
            <CustomSelect
              value={statusFilter}
              onChange={(value) => setStatusFilter(value)}
              options={[
                { value: '', label: 'All' },
                { value: 'completed', label: 'Completed' },
                { value: 'pending', label: 'Pending' },
                { value: 'failed', label: 'Failed' },
                { value: 'refunded', label: 'Refunded' },
              ]}
              aria-label="Status"
            />
          </div>

          {/* Purchased By Filter */}
          <div>
            <label htmlFor="purchased-by-filter" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Filter by Purchase Source
            </label>
            <CustomSelect
              value={purchasedByFilter}
              onChange={(value) => setPurchasedByFilter(value)}
              options={[
                { value: '', label: 'All' },
                { value: 'agent', label: 'Agent' },
                { value: 'user', label: 'User' },
              ]}
              aria-label="Purchased By"
            />
          </div>
        </div>

        {/* Date range error */}
        {dateRangeError && (
          <div className="mt-3 text-red-600 text-sm">{dateRangeError}</div>
        )}

        {/* Filter buttons */}
        <div className="flex gap-3 mt-4">
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 transition"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Empty state */}
      {purchases.length === 0 && !loading && (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <p className="text-gray-600 dark:text-gray-400 text-lg font-semibold mb-2">No purchases found</p>
          <p className="text-gray-500 dark:text-gray-400">You haven't purchased any domains yet</p>
        </div>
      )}

      {/* Purchases table */}
      {purchases.length > 0 && (
        <>
          <div className="bg-white dark:bg-cap-dark border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" role="table">
                <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                  <tr role="row">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300" scope="col">
                      <span>Domain</span>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('price')}
                      scope="col"
                    >
                      <div className="flex items-center gap-1">
                        <span>Price</span>
                        {sortField === 'price' && (
                          <span className={`sort-icon sort-${sortOrder}`}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300" scope="col">
                      <span>Status</span>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                      onClick={() => handleSort('purchased_at')}
                      scope="col"
                    >
                      <div className="flex items-center gap-1">
                        <span>Purchased Date</span>
                        {sortField === 'purchased_at' && (
                          <span className={`sort-icon sort-${sortOrder}`}>
                            {sortOrder === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300" scope="col">
                      <span>Purchased By</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {purchases.map((purchase) => (
                    <tr key={purchase.purchase_id} className="hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900">
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleDomainClick(purchase.purchase_id)}
                          className="text-primary-600 hover:text-primary-800 hover:underline font-medium truncate max-w-xs"
                        >
                          {purchase.domain}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {formatPrice(purchase.price, purchase.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={getStatusClass(purchase.status)}>
                          {purchase.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {formatDate(purchase.purchased_at)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-gray-900 dark:text-warm-white">
                            {purchase.purchased_by === 'agent' ? 'Agent' : 'User'}
                          </span>
                          {purchase.purchased_by === 'agent' && purchase.purchase_context.agent_name && (
                            <>
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                {purchase.purchase_context.agent_name}
                              </span>
                              {purchase.purchase_context.orchestration_name && (
                                <span className="text-sm text-gray-500 dark:text-gray-400">
                                  {purchase.purchase_context.orchestration_name}
                                </span>
                              )}
                              {purchase.purchase_context.run_instance_id && (
                                <a
                                  href={`/workflows/run/${purchase.purchase_context.run_instance_id}`}
                                  className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
                                >
                                  View Run Details
                                </a>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-cap-dark text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  aria-label="Next page"
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-cap-dark text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PurchaseHistory;
